//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helpers
import { capFirstChar } from 'src/helpers/utilities';
import {
	dateFormat,
	getDateByTimeZone,
	getTimeZoneOffset,
	timeFormatUS,
	time_zones,
} from 'src/helpers/date_helper';
import { is_gen_2 } from 'src/helpers/interface_helpers';
import { addonInputTypes } from 'src/helpers/addons/addon_helpers';
import { requestEditDevice, requestEditSharedDevice } from 'src/components/Lists/list-helpers';
//Stores
import { devices, devices_settings, user_data } from 'src/stores';
//Types
import type { Device } from 'src/types/Device';
import type { ChartConfig, TimeZoneInterface } from 'src/types/Chart';

const _format = get(format);

const clickAction = {
	day: 'hour',
	week: 'day',
	month: 'day',
	year: 'month',
};

const rangeIntervalFormat = {
	hour: 'day',
	day: 'month',
	month: 'year',
	year: 'year',
};

export const getClickAction = (period: string) => {
	return clickAction[period];
};

export const getRangeValue = (rangeInterval: string) => {
	return rangeIntervalFormat[rangeInterval];
};

export const updateDeviceInterfaceData = async (device) => {
	let res;
	if (device.shared) {
		res = await requestEditSharedDevice(device);
	} else {
		res = await requestEditDevice(device);
	}

	return res;
};

export const formatSeriesLabel = (value: string) => {
	let formatedName = '';
	const split_name = value.split('_');
	if (!isNaN(Number(split_name[split_name.length - 1]))) {
		split_name.pop();
		formatedName = _format('chart_legend_' + split_name.join('_'), {
			default: value.replace(/_/g, ' '),
		});
	} else {
		formatedName = _format('chart_legend_' + value, {
			default: value == 'batt_invertor' ? 'battery & invertor' : value.replace(/_/g, ' '),
		});
	}
	return capFirstChar(formatedName);
};

export const getChartSensorName = (chartsConfig: ChartConfig, showAlias = true): string => {
	const id = chartsConfig.deviceID;
	let alias = addonInputTypes?.[chartsConfig.name]?.alias || '';
	let sensorName = '';
	if (Number(chartsConfig.sensorID) >= 100) {
		const deviceSettings = devices_settings.getByRealId(id);
		const component = `${chartsConfig.name}:${chartsConfig.sensorID}`;
		sensorName = deviceSettings?.[component]?.name || '';
		if (
			typeof chartsConfig.deviceChannel === 'string' &&
			deviceSettings?.[chartsConfig.deviceChannel]
		) {
			sensorName = deviceSettings[chartsConfig.deviceChannel]?.name || '';
			alias = addonInputTypes?.[chartsConfig.title]?.alias || '';
		}
	} else if (Number(chartsConfig.sensorID) >= 0) {
		const extNames = get(devices)[id]?.external_sensors_names || {};
		const component = `${chartsConfig.sensorID}_${chartsConfig.name}`;
		const extName = extNames[component];
		if (extName && extName.name !== 'unnamed') {
			sensorName = extName.name;
		}
	}
	if (showAlias && !sensorName && alias && chartsConfig?.sensorID) {
		sensorName += `${alias} (${chartsConfig.sensorID})`;
	}
	return sensorName;
};

export const getDeviceTimeZoneDate = (device: Device): TimeZoneInterface => {
	const now = new Date(),
		result = { tz: '', date: now, timeZoneOffset: 0, time: '' };
	if (device) {
		const settings = devices_settings.getByRealId(device.id);
		result.tz = is_gen_2(device) ? settings?.sys?.location?.tz : settings?.timezone;
		if (!time_zones.includes(result?.tz)) {
			result.tz = get(user_data).timezone;
		}
		result.date = getDateByTimeZone(result.tz);
		result.time = dateFormat(result.date, timeFormatUS() ? 'h:i A' : 'H:i');
		result.timeZoneOffset = getTimeZoneOffset(result.tz);
	}
	return result;
};

export const checkForStatisticError = (value: number): boolean => {
	return Math.abs(value) === 999 || Math.abs(value) === Infinity || value === null || isNaN(value);
};

export const deepCloneObj = (obj: any) => {
	let clone = Object.assign({}, obj);
	Object.keys(clone).forEach(
		(key) =>
			(clone[key] =
				typeof obj[key] === 'object' && obj[key] != null ? deepCloneObj(obj[key]) : obj[key]),
	);
	return Array.isArray(obj) ? (clone.length = obj.length) && Array.from(clone) : clone;
};
